import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import { postContact, createLead } from "@/lib/api";

const field =
  "w-full bg-transparent border-b border-[#47394E]/25 py-3 font-body text-[#47394E] outline-none placeholder:text-[#47394E]/40 focus:border-[#A8B39B] transition-colors";

const BOOK = [
  ["B", "Build Your Revenue Foundation", "Get clear on the numbers, goals, ideal client, what's working, what's not, and where revenue is actually coming from."],
  ["O", "Own Your Positioning & Value", "Know exactly who you serve, what makes you different, and why the right couple should choose you."],
  ["O", "Open More Relationship Revenue", "Build intentional relationships with venues, pros, and past clients so referrals become a strategy, not a hope."],
  ["K", "Keep Leads Moving", "Strengthen response time, follow-up, inquiry systems, and booking experience so opportunities turn into revenue."],
];

const OFFERS = [
  {
    name: "Revenue Intensive",
    price: "$997",
    desc: "I dig into your business, find where the gaps are, and build a personalized 30-day action plan — then we follow up on what's working and decide your next moves.",
    points: ["3 × 1:1 Zoom calls", "30-day action plan", "Custom tools (lead trackers, follow-up sequences)", "Accountability follow-up + next steps"],
  },
  {
    name: "Private 1:1 Business Mentorship",
    price: "",
    note: "6-month membership",
    desc: "An experienced strategist in your corner for the opportunities, challenges, and decisions happening in your business right now.",
    points: ["Two private sessions each month", "Email support between sessions", "Personalized action steps + accountability", "Includes Devoted VIP membership ($550 value)"],
  },
  {
    name: "90-Minute Strategy Session",
    price: "$295",
    desc: "A focused session for when you need clarity, direction, and expert insight right now — pricing, marketing, leads, systems, positioning, or a decision you've been stuck on.",
    points: ["Single 90-minute Zoom session", "Actionable guidance + fresh ideas", "A clearer path forward, fast"],
  },
];

function WaitlistForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await postContact({ ...form, source: "group-coaching-waitlist", interest: "The B.O.O.K. Method", subject: "Group Coaching interest (The B.O.O.K. Method)" });
      toast.success("You're on the interest list — Jamie will be in touch as the group forms.");
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#47394E] text-[#E8DCCF] p-10" data-testid="waitlist-success">
        <span className="font-display text-5xl text-[#A8B39B]">✦</span>
        <h3 className="font-display text-3xl mt-4">Application received!</h3>
        <p className="font-body text-[#E8DCCF]/80 mt-3">Thank you, {form.name.split(" ")[0] || "friend"}. Jamie will personally review your application and reach out to confirm your spot in the Founding Cohort.</p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="waitlist-form" className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-6">
        <input required placeholder="Full name" data-testid="waitlist-name" value={form.name} onChange={set("name")} className={field} />
        <input required type="email" placeholder="Email address" data-testid="waitlist-email" value={form.email} onChange={set("email")} className={field} />
      </div>
      <input placeholder="Your business / role" data-testid="waitlist-company" value={form.company} onChange={set("company")} className={field} />
      <textarea rows={3} placeholder="What are you hoping to work on? (optional)" data-testid="waitlist-message" value={form.message} onChange={set("message")} className={`${field} resize-none`} />
      <button type="submit" disabled={loading} data-testid="waitlist-submit" className="rounded-full bg-[#47394E] text-[#E8DCCF] px-9 py-3.5 font-medium hover:bg-[#A8B39B] hover:text-[#47394E] transition-colors disabled:opacity-60">
        {loading ? "Sending…" : "Apply for the Founding Cohort"}
      </button>
    </form>
  );
}

function ClaimForm({ source, cta, testid, dark }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await createLead({ email, source });
      toast.success("You're on the list — thank you!");
      setDone(true);
      setEmail("");
    } catch {
      toast.error("Please enter a valid email and try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) return <p className="font-body text-[#47394E] font-semibold" data-testid={`${testid}-success`}>✦ You're on the list — check your inbox soon.</p>;

  return (
    <form onSubmit={submit} data-testid={testid} className="flex flex-col sm:flex-row gap-3 max-w-md">
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address" data-testid={`${testid}-email`}
        className="flex-1 bg-transparent border-b border-[#47394E]/30 py-3 font-body text-[#47394E] outline-none placeholder:text-[#47394E]/40 focus:border-[#B7926A] transition-colors" />
      <button type="submit" disabled={loading} data-testid={`${testid}-submit`}
        className="shrink-0 rounded-full bg-[#47394E] text-[#E8DCCF] px-7 py-3 font-medium hover:bg-[#B7926A] transition-colors disabled:opacity-60">
        {loading ? "…" : cta}
      </button>
    </form>
  );
}

export default function Coaching() {
  useEffect(() => {
    if (window.location.hash === "#apply") {
      setTimeout(() => document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, []);
  return (
    <div data-testid="coaching-page" className="pt-24">
      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <p className="overline text-[#A8B39B] mb-6">Coaching · Business Relationship & Revenue Mentor</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-[5.5rem] text-[#47394E] leading-[0.92] tracking-tight">
            <MaskedLines lines={["Grow with strategy,", "not more hustle."]} />
          </h1>
        </div>
        <div className="lg:col-span-4">
          <p className="font-body text-lg text-[#47394E]/75 leading-relaxed">
            You've built the business. You know your craft. But that doesn't mean revenue always feels
            predictable. With 25+ years in the wedding industry, I help you move from guessing to
            knowing — from scattered marketing to intentional strategy.
          </p>
        </div>
      </section>

      <div className="bg-[#A8B39B]/40 h-2" />

      {/* Group Coaching — The B.O.O.K. Method */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28" data-testid="group-coaching">
        <Reveal className="mb-12 max-w-3xl">
          <p className="overline text-[#A8B39B] mb-4">Founding Cohort · Limited to 10 wedding pros</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#47394E] tracking-tight leading-none">
            The B.O.O.K. Method
          </h2>
          <p className="font-body text-lg text-[#47394E]/75 mt-6 leading-relaxed">
            A 16-week group coaching program for established wedding professionals ready to create more
            predictable revenue — with coaching, accountability, and the support of other wedding pros
            so you're not just learning what to do, you're actually implementing it.
          </p>
        </Reveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#EAE3D8]">
          {BOOK.map(([letter, title, body], i) => (
            <Reveal key={title} delay={(i % 4) * 0.08}>
              <div className="bg-white p-8 h-full">
                <span className="font-display text-6xl text-[#A8B39B]">{letter}</span>
                <h3 className="font-display text-xl text-[#47394E] mt-3 leading-snug">{title}</h3>
                <p className="font-body text-sm text-[#47394E]/70 mt-3 leading-relaxed">{body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-10 flex flex-wrap items-baseline gap-x-6 gap-y-2">
          <p className="font-display text-2xl text-[#47394E]">Founding Cohort Investment: $297<span className="text-base font-body text-[#47394E]/60">/month for 4 months</span></p>
          <span className="font-body text-sm text-[#47394E]/60">Future cohorts will be priced higher.</span>
        </Reveal>
      </section>

      {/* Application */}
      <section id="apply" className="bg-white py-20 lg:py-28 scroll-mt-24" data-testid="waitlist-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-5">
            <p className="overline text-[#B7926A] mb-4">B.O.O.K. Method Founding Cohort</p>
            <h2 className="font-display text-4xl sm:text-5xl text-[#47394E] tracking-tight leading-none">
              Limited to 10 wedding professionals.
            </h2>
            <p className="font-body text-[#47394E]/75 mt-6 leading-relaxed">
              Investment: <strong>$297/month for 4 months</strong>. Future cohorts will be priced
              higher — founding pricing is reserved for the first 10 participants.
            </p>
            <p className="font-body text-[#47394E]/75 mt-4 leading-relaxed">
              Apply below and Jamie will personally review your application to make sure the program is
              a good fit for where your business is today. Applications close when all 10 spots are
              filled.
            </p>
          </Reveal>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal><WaitlistForm /></Reveal>
          </div>
        </div>
      </section>

      {/* Additional Offerings */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28" data-testid="offerings">
        <Reveal className="mb-14">
          <p className="overline text-[#A8B39B] mb-4">Additional ways to work together</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#47394E] tracking-tight leading-none max-w-2xl">
            Prefer 1:1 support?
          </h2>
        </Reveal>

        <div className="space-y-px bg-[#EAE3D8]">
          {OFFERS.map((s, i) => (
            <Reveal key={s.name} delay={i * 0.06}>
              <div className="bg-white grid lg:grid-cols-12 gap-8 p-8 lg:p-12 items-start">
                <div className="lg:col-span-5">
                  <h3 className="font-display text-3xl text-[#47394E]">{s.name}</h3>
                  {s.price ? (
                    <p className="font-display text-2xl text-[#B7926A] mt-2">{s.price}{s.note ? <span className="text-sm font-body text-[#47394E]/60"> · {s.note}</span> : null}</p>
                  ) : (
                    <p className="font-body text-base italic text-[#B7926A] mt-2">Let's hop on a call to find the right fit.</p>
                  )}
                  <p className="font-body text-[#47394E]/70 mt-4 leading-relaxed">{s.desc}</p>
                </div>
                <ul className="lg:col-span-4 space-y-3 font-body text-[#47394E]/80">
                  {s.points.map((p) => (
                    <li key={p} className="flex gap-3 border-b border-[#D9C3A8] pb-3"><span className="text-[#A8B39B]">✦</span> {p}</li>
                  ))}
                </ul>
                <div className="lg:col-span-3 lg:text-right">
                  <Link to="/booking" data-testid={`offer-cta-${i}`} className="inline-flex items-center gap-2 rounded-full bg-[#47394E] text-[#E8DCCF] px-7 py-3.5 font-medium hover:bg-[#A8B39B] hover:text-[#47394E] transition-colors">
                    Book a call <ArrowUpRight size={16} />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Freebie */}
      <section className="bg-[#47394E] text-[#E8DCCF] py-20 lg:py-28 grain relative" data-testid="lead-magnets">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="bg-white text-[#47394E] p-10 h-full">
              <p className="overline text-[#B7926A] mb-3">Free guide</p>
              <h3 className="font-display text-3xl text-[#47394E]">Where Your Next 5 Bookings Are Hiding</h3>
              <p className="font-body text-[#47394E]/70 mt-4 mb-6">
                A quick clarity exercise, a relationship audit, and a simple framework to identify
                five people to reach out to — with clear next steps so you always know what to say.
              </p>
              <ClaimForm source="freebie" cta="Tell me where to send the guide" testid="freebie-form" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
